import type {
  EducationalAdvisorRepository,
  EducationalAdvisorBatchStudio,
  StudentEvaluationVM,
  TrainingItemEvaluationVM,
  EducationalAdvisorPendingActionsVM,
  SubmitTrainingItemEvaluationCommand,
  EnrollStudentToBatchBody,
  SubmitBatchParticipationInvoiceBody,
  CompleteLegacyBatchBody,
  CancelBatchEnrollmentBody,
  CancelBatchParticipationBody,
  CreateBatchBody,
  UpdateBatchBody,
  ToggleBatchPublicVisibilityBody,
  EducationalAdvisorListVM,
  BatchListItemVM,
  EABatchDetailVM,
  BatchStudent,
  ExternalTeacherVM,
  ExternalTeacherEmailCheckVM,
  CreateExternalTeacherBody,
  UpdateExternalTeacherBody,
} from "@shared-kernel/application/ports/educational-advisor/educational-advisor-repository";
import type { Nullable } from "@shared-kernel/core/types/nullable";
import { HttpGateway } from "@shared-kernel/secondary/http-gateway";
import type { TokenStorageGateway } from "@shared-kernel/application/ports/token-storage-gateway";
import backendConfig from "@config/backend";

export class BackendEducationalAdvisorRepository extends HttpGateway implements EducationalAdvisorRepository {
  private _route: string = "v1/educational-advisor";

  constructor(tokenStorageGateway: TokenStorageGateway) {
    super(backendConfig.url, tokenStorageGateway);
  }

  async getStudents(): Promise<BatchStudent[]> {
    const { data } = await this._instance.get<BatchStudent[]>(`${this._route}/students`);
    return data;
  }

  async getStudentByEmail(email: string): Promise<Nullable<BatchStudent>> {
    const { data } = await this._instance.get<Nullable<BatchStudent>>(`${this._route}/students/${email}`);
    return data;
  }

  async getEvaluationsGroupedByStudent(): Promise<StudentEvaluationVM[]> {
    const response = await this._instance.get<StudentEvaluationVM[]>(`${this._route}/evaluations-by-student`);
    return response.data;
  }

  async getEvaluationsByTrainingItem(studentId: string): Promise<TrainingItemEvaluationVM[]> {
    const response = await this._instance.get<TrainingItemEvaluationVM[]>(
      `${this._route}/students/${studentId}/evaluations-by-training-item`,
    );
    return response.data;
  }

  async hasPendingActions(): Promise<EducationalAdvisorPendingActionsVM> {
    const response = await this._instance.get<EducationalAdvisorPendingActionsVM>(`${this._route}/has-pending-actions`);
    return response.data;
  }

  async listBatchStudios(): Promise<EducationalAdvisorBatchStudio[]> {
    const { data } = await this._instance.get<EducationalAdvisorBatchStudio[]>(`${this._route}/studios`);
    return data;
  }

  async educationalAdvisorList(): Promise<EducationalAdvisorListVM> {
    const { data } = await this._instance.get<EducationalAdvisorListVM>(`${this._route}/educational-advisors`);
    return data;
  }

  async batchList(): Promise<BatchListItemVM[]> {
    const { data: batches } = await this._instance.get<BatchListItemVM[]>(`${this._route}/batches`);
    return batches;
  }

  async batchDetail(id: string): Promise<EABatchDetailVM> {
    const { data: batch } = await this._instance.get<EABatchDetailVM>(`${this._route}/batches/${id}`);
    return batch;
  }

  async createBatch(body: CreateBatchBody): Promise<void> {
    await this._instance.post(`${this._route}/batches`, body);
  }

  async updateBatch(body: UpdateBatchBody): Promise<void> {
    const { batchId, ...rest } = body;
    await this._instance.put(`${this._route}/batches/${batchId}`, rest);
  }

  async toggleBatchPublicVisibility(body: ToggleBatchPublicVisibilityBody): Promise<void> {
    await this._instance.patch(`${this._route}/batches/${body.batchId}/public-visibility`, {
      isPublicVisible: body.isPublicVisible,
    });
  }

  async deleteBatch(batchId: string): Promise<void> {
    await this._instance.delete(`${this._route}/batches/${batchId}`);
  }

  async enrollStudent(body: EnrollStudentToBatchBody): Promise<void> {
    const { batchId, ...enrollmentBody } = body;
    await this._instance.post(`${this._route}/batches/${batchId}/batch-enrollments`, enrollmentBody);
  }

  async submitParticipationInvoice(body: SubmitBatchParticipationInvoiceBody): Promise<void> {
    const { participationId, ...invoiceBody } = body;
    await this._instance.post(`${this._route}/batch-participations/${participationId}:submit-invoice`, invoiceBody);
  }

  async submitTrainingItemEvaluation(command: SubmitTrainingItemEvaluationCommand): Promise<void> {
    await this._instance.post(`${this._route}/training-items:evaluate`, command);
  }

  async cancelEnrollment(command: CancelBatchEnrollmentBody): Promise<void> {
    await this._instance.post(`${this._route}/batch-enrollments/${command.enrollmentId}:cancel`, {
      cancellationMessage: command.cancellationMessage,
    });
  }

  async cancelParticipation(command: CancelBatchParticipationBody): Promise<void> {
    await this._instance.post(`${this._route}/batch-participations/${command.participationId}:cancel`, {
      cancellationMessage: command.cancellationMessage,
    });
  }

  async initializeEnrollmentContract(enrollmentId: string): Promise<string> {
    const response = await this._instance.post<string>(`${this._route}/batch-enrollments/${enrollmentId}:initialize-contract`);
    return response.data;
  }

  async completeLegacyBatch(body: CompleteLegacyBatchBody): Promise<void> {
    const { batchId, ...rest } = body;
    await this._instance.post(`${this._route}/batches/${batchId}:complete-legacy`, rest);
  }

  async listExternalTeachers(): Promise<ExternalTeacherVM[]> {
    const { data } = await this._instance.get<ExternalTeacherVM[]>(`${this._route}/external-teachers`);
    return data;
  }

  async getExternalTeacher(id: string): Promise<ExternalTeacherVM> {
    const { data } = await this._instance.get<ExternalTeacherVM>(`${this._route}/external-teachers/${id}`);
    return data;
  }

  async checkExternalTeacherEmail(email: string): Promise<ExternalTeacherEmailCheckVM> {
    const { data } = await this._instance.get<ExternalTeacherEmailCheckVM>(`${this._route}/external-teachers:email-check`, {
      params: { email },
    });
    return data;
  }

  async createExternalTeacher(body: CreateExternalTeacherBody): Promise<string> {
    const { data } = await this._instance.post<string>(`${this._route}/external-teachers`, body);
    return data;
  }

  async updateExternalTeacher(body: UpdateExternalTeacherBody): Promise<void> {
    const { id, ...rest } = body;
    await this._instance.put(`${this._route}/external-teachers/${id}`, rest);
  }

  async deleteExternalTeacher(id: string): Promise<void> {
    await this._instance.delete(`${this._route}/external-teachers/${id}`);
  }
}
